import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ListChecksIcon, PaperclipIcon, SendIcon, XIcon } from 'lucide-react';
import { cn, toast } from '@noor/lib';
import { IconButton } from '@noor/ui';
import { FILE_ONLY_MESSAGE, isChatAcquired, type Chat } from '@/entities/conversation';
import { addAttachment, hasSendableContent } from '../model/attachments';
import { useAcquireChat, useSendMessage } from '../model/use-chat-mutations';
import { AcquireChatDialog } from './acquire-chat-dialog';
import { MacrosPanel } from './macros-panel';
import { QuickMessagesPopover } from './quick-messages-popover';

interface ChatComposerProps {
  /** The open chat (detail). Mount with `key={chat.id}` so a draft never leaks across chats. */
  chat: Chat;
}

type Panel = 'macros' | null;

/**
 * The reply bar of the open chat (cpanel `ChatMessageInput`): a growing
 * textarea (Enter sends, Shift/Ctrl+Enter = new line), emoji, operator note,
 * macros, quick messages and a single image attachment (≤ 10 MB).
 *
 * Replying to a chat nobody owns first asks to take it (`acquire`); the draft is
 * sent right after the chat is acquired. A failed send keeps the draft.
 */
export function ChatComposer({ chat }: ChatComposerProps) {
  const { t } = useTranslation('inbox');
  const chatId = chat.id;
  const acquired = isChatAcquired(chat);

  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [panel, setPanel] = useState<Panel>(null);
  const [acquireOpen, setAcquireOpen] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replacingRef = useRef<File | null>(null);

  const send = useSendMessage(chatId);
  const acquire = useAcquireChat(chatId);

  // Close the macros / note drawers on a click outside the composer — but not on
  // clicks inside a dialog opened from them (macro editor, delete confirm).
  useEffect(() => {
    if (panel === null) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (rootRef.current?.contains(target)) return;
      if (target.closest('[role="dialog"],[role="alertdialog"]')) return;
      setPanel(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [panel]);

  const insertAtCaret = (text: string) => {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? message.length;
    const end = el?.selectionEnd ?? message.length;
    setMessage(message.slice(0, start) + text + message.slice(end));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + text.length, start + text.length);
    });
  };

  const sendDraft = () => {
    send.mutate(
      { message: message || FILE_ONLY_MESSAGE, files },
      {
        onSuccess: () => {
          setMessage('');
          setFiles([]);
        },
      },
    );
  };

  const submit = () => {
    if (!message && files.length === 0) return;
    if (!acquired) {
      setAcquireOpen(true);
      return;
    }
    if (!hasSendableContent(message, files)) return;
    sendDraft();
  };

  const confirmAcquire = () => {
    acquire.mutate(undefined, {
      onSuccess: () => {
        setAcquireOpen(false);
        if (message || files.length > 0) sendDraft();
      },
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
    if (event.shiftKey) return; // the browser inserts the new line
    event.preventDefault();
    if (event.ctrlKey || event.metaKey) {
      insertAtCaret('\n');
      return;
    }
    submit();
  };

  const pickFile = (replacing: File | null) => {
    replacingRef.current = replacing;
    fileInputRef.current?.click();
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const incoming = event.target.files?.[0];
    event.target.value = '';
    if (!incoming) return;
    const verdict = addAttachment(files, incoming, replacingRef.current);
    if (!verdict.ok) {
      toast.error(t(`attachment.${verdict.reason}`));
      return;
    }
    setFiles(verdict.files);
  };

  const togglePanel = (next: Exclude<Panel, null>) =>
    setPanel((current) => (current === next ? null : next));

  const canSubmit = (message !== '' || files.length > 0) && !send.isPending;

  return (
    <div ref={rootRef} className="relative shrink-0 border-t border-border bg-card">
      {panel !== null ? (
        <div className="absolute inset-x-0 bottom-full z-10 bg-card shadow-lg">
          <MacrosPanel onSelect={setMessage} />
        </div>
      ) : null}

      {files.length > 0 ? (
        <div className="flex flex-wrap gap-2 px-3 pt-3">
          {files.map((file) => (
            <span
              key={file.name}
              className="inline-flex max-w-52 items-center gap-1 rounded-full border border-border bg-muted py-1 pr-1 pl-3 text-xs font-medium"
            >
              <button
                type="button"
                className="truncate focus-visible:outline-none"
                title={file.name}
                onClick={() => pickFile(file)}
              >
                {file.name}
              </button>
              <button
                type="button"
                aria-label={t('composer.removeFile')}
                className="flex size-5 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-card hover:text-destructive"
                onClick={() =>
                  setFiles((prev) => prev.filter((f) => f.name !== file.name))
                }
              >
                <XIcon className="size-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <div className="flex items-end gap-2 p-3">
        <div className="flex shrink-0 gap-1">
          <IconButton
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              'rounded-full',
              panel === 'macros' && 'bg-primary/10 text-primary',
            )}
            aria-label={t('composer.macros')}
            aria-pressed={panel === 'macros'}
            onClick={() => togglePanel('macros')}
          >
            <ListChecksIcon className="size-5" />
          </IconButton>
        </div>

        <textarea
          ref={textareaRef}
          rows={1}
          value={message}
          disabled={files.length > 0}
          placeholder={t('composerPlaceholder')}
          aria-label={t('composerPlaceholder')}
          onChange={(event) => setMessage(event.target.value)}
          onKeyDown={onKeyDown}
          className="[field-sizing:content] max-h-40 min-h-10 min-w-0 flex-1 resize-none rounded-xl bg-muted px-3 py-2.5 text-sm leading-snug text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-60"
        />

        <div className="flex shrink-0 gap-1">
          <IconButton
            type="button"
            variant="ghost"
            size="sm"
            className="rounded-full"
            aria-label={t('actions.attach')}
            onClick={() => pickFile(null)}
          >
            <PaperclipIcon className="size-5" />
          </IconButton>
          <IconButton
            type="button"
            size="sm"
            className="rounded-full"
            aria-label={t('actions.send')}
            disabled={!canSubmit}
            onClick={submit}
          >
            <SendIcon className="size-5" />
          </IconButton>
          <QuickMessagesPopover onPick={setMessage} />
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFileChange}
        tabIndex={-1}
        aria-hidden="true"
      />

      <AcquireChatDialog
        open={acquireOpen}
        pending={acquire.isPending}
        onOpenChange={setAcquireOpen}
        onConfirm={confirmAcquire}
      />
    </div>
  );
}
